import { ErrorLogger } from "../../utils/Logger";
import { Buyer } from "../../Models/Buyer";
import { BuyerAPIHandler } from "../NetworkClient/BackendAPIs/BuyerAPIHandler";
import { SessionManager } from "../SessionManager/SessionManager";
import { store } from "../../redux/store";
import { setUnreadCount } from "../../redux/chat/chatSlice";

const NOTIFICATION_SOUND_PATH = `${process.env.PUBLIC_URL}/assets/sounds/gg_notification_default.mp3`;

export class ChatManager {

  public static shared = new ChatManager();

  private isUserConnected = false;
  private eventListenersRegistered = false;

  private updateUnreadCount = (newUnreadCount: number) => {
    // Get previous unread count from Redux store
    const previousUnreadCount = store.getState().chat.unreadCount;
    
    // Directly dispatch to Redux store
    store.dispatch(setUnreadCount(newUnreadCount));

    // Play notification audio only if unread count increased
    if (newUnreadCount > previousUnreadCount) {
      const notificationSound = new Audio(NOTIFICATION_SOUND_PATH);
      notificationSound.play().catch(() => {});
    }
  };

  public isConnected = (): boolean => {
    return this.isUserConnected;
  };

  // Public method to manually refresh unread count from client
  public refreshUnreadCount = () => {
    const client = SessionManager.shared.getChatClient();
    if (client.user?.total_unread_count !== undefined) {
      this.updateUnreadCount(client.user.total_unread_count);
    }
  };

  public makeUserConnection = async (buyer: Buyer) => {
    const client = SessionManager.shared.getChatClient();
    
    // Only connect if not already connected
    if (!this.isUserConnected) {
      await client.connectUser(
        {
          id: buyer.id,
          name: buyer.fullName,
          image: buyer.profileImage
        },
        buyer.chatToken
      );
      this.isUserConnected = true;
      this.updateUnreadCount(client.user?.total_unread_count || 0);
      this.setupNotificationListeners();
    }

    // Re-sync unread count from current client state
    if (client.user?.total_unread_count !== undefined) {
      this.updateUnreadCount(client.user.total_unread_count);
    }
  };

  // Public method to setup notification listeners for unread count updates
  public setupNotificationListeners = () => {
    const client = SessionManager.shared.getChatClient();

    // Only register event listeners once
    if (!this.eventListenersRegistered) {
      // Listen to ALL events to catch unread count updates
      // This handles both notification events (when not watching) and channel events (when watching)
      client.on((event: any) => {
        if (event.total_unread_count !== undefined) {
          this.updateUnreadCount(event.total_unread_count);
        }
      });
      
      this.eventListenersRegistered = true;
    }
  };

  // Send message to a specific channel
  public sendMessageToChannel = async (channelId: string, customMessage: object, message: string="Custom Message") => {
    try {
      const buyer = SessionManager.shared.buyer || await BuyerAPIHandler.shared.getBuyerByAuthToken()
      const client = SessionManager.shared.getChatClient();
      await this.makeUserConnection(buyer);

      const channel = await client.channel('messaging', channelId);
      await channel.watch(); // ensure the channel is ready
      await channel?.sendMessage({
        text: message,
        ...customMessage
      });

    } catch (err) {
      ErrorLogger.log(err, 'ChatManager.sendMessageToChannel');
    }
  };

}



